import { api } from '../../lib/api.js';
import { BaseComponent, html, nothing } from '../../lib/lit.js';
import './community-markdown-input.js';

/**
 * The types a person can file, in the order they're offered. The values are the
 * API's stored enum and must match `feedback.Types` on the server; the labels
 * are ours to word.
 */
export const FEEDBACK_TYPES = [
    { value: 'bug', label: 'Bug', icon: 'bi-bug', hint: 'Something is broken or behaving wrongly' },
    {
        value: 'feature',
        label: 'Feature request',
        icon: 'bi-stars',
        hint: 'Something you want that does not exist yet',
    },
    {
        value: 'idea',
        label: 'Idea',
        icon: 'bi-lightbulb',
        hint: 'A direction worth exploring, not yet a request',
    },
    {
        value: 'general',
        label: 'General feedback',
        icon: 'bi-chat-left-text',
        hint: 'Anything else you want to tell us',
    },
];

/**
 * <community-give-feedback> — the filing form.
 *
 * Emits `item-filed` with the created item on success so the shell can prepend
 * it to the list without refetching, and `cancel` when dismissed.
 *
 * `dismissible` controls whether it can be closed. It is false in the empty
 * state — where the form IS the page and there is nothing to go back to — and
 * true when opened from the Give feedback button above an existing list.
 */
export class CommunityGiveFeedback extends BaseComponent {
    static properties = {
        products: { type: Array },
        dismissible: { type: Boolean },
        heading: { type: String },
        _type: { state: true },
        _product: { state: true },
        _title: { state: true },
        _body: { state: true },
        _submitting: { state: true },
        _uploading: { state: true },
        _error: { state: true },
    };

    constructor() {
        super();
        /** @type {Array<{slug: string, name: string}>} */
        this.products = [];
        this.dismissible = false;
        this.heading = 'Give feedback';
        this._type = 'bug';
        this._product = '';
        this._title = '';
        this._body = '';
        this._submitting = false;
        this._uploading = false;
        this._error = '';
    }

    /**
     * The product to file against, or '' for "not sure".
     *
     * It used to default to the only registered product when there was exactly
     * one, which quietly filed everything against the VTT. With several products
     * and an explicit "Not sure", an unanswered question stays unanswered and
     * staff triage it.
     */
    get _selectedProduct() {
        return this._product;
    }

    /**
     * Neither the description nor the product is checked here: both are
     * optional, and the API stores an omitted one as NULL. Only the title gates
     * submission, matching what the server actually rejects.
     *
     * An in-flight upload does gate it, though: sending now would file the
     * placeholder as literal text, and on an item with a product that text
     * relays to a GitHub issue the reporter cannot see or edit.
     */
    get _canSubmit() {
        return !this._submitting && !this._uploading && this._title.trim() !== '';
    }

    async _submit(event) {
        event.preventDefault();
        if (!this._canSubmit) return;

        this._submitting = true;
        this._error = '';
        try {
            const item = await api.post('/items', {
                product: this._selectedProduct,
                type: this._type,
                title: this._title.trim(),
                body: this._body.trim(),
            });
            this.dispatchEvent(
                new CustomEvent('item-filed', { detail: item, bubbles: true, composed: true }),
            );
            this._title = '';
            this._body = '';
            this._type = 'bug';
        } catch (err) {
            // The rate cap is the one failure a person can act on, so say what
            // it means rather than showing a status code.
            this._error =
                /** @type {any} */ (err).status === 429
                    ? "You've filed a lot of feedback in the last hour — give it a little while and try again."
                    : /** @type {any} */ (err).message || String(err);
        } finally {
            this._submitting = false;
        }
    }

    render() {
        const selected = FEEDBACK_TYPES.find((t) => t.value === this._type);

        return html`
            <div class="card shadow-sm border-0 mb-4">
                <div class="card-body p-4">
                    <div class="d-flex align-items-start justify-content-between mb-3">
                        <h2 class="h5 mb-0">${this.heading}</h2>
                        ${
                            this.dismissible
                                ? html`<button
                                      type="button"
                                      class="btn-close"
                                      aria-label="Close"
                                      @click=${() => this.dispatchEvent(new CustomEvent('cancel'))}
                                  ></button>`
                                : nothing
                        }
                    </div>

                    <form @submit=${this._submit}>
                        <div class="mb-3">
                            <div class="btn-group w-100" role="group" aria-label="Feedback type">
                                ${FEEDBACK_TYPES.map(
                                    (type) => html`
                                        <input
                                            type="radio"
                                            class="btn-check"
                                            name="feedback-type"
                                            id="type-${type.value}"
                                            autocomplete="off"
                                            .checked=${this._type === type.value}
                                            @change=${() => {
                                                this._type = type.value;
                                            }}
                                        />
                                        <label
                                            class="btn btn-outline-secondary"
                                            for="type-${type.value}"
                                        >
                                            <i class="bi ${type.icon} me-1"></i>${type.label}
                                        </label>
                                    `,
                                )}
                            </div>
                            <div class="form-text">${selected?.hint ?? ''}</div>
                        </div>

                        <div class="mb-3">
                            <label class="form-label" for="product">
                                Product <span class="text-muted fw-normal">(optional)</span>
                            </label>
                            <select
                                id="product"
                                class="form-select"
                                .value=${this._product}
                                @change=${(e) => {
                                    this._product = e.target.value;
                                }}
                            >
                                <option value="">Not sure</option>
                                ${this.products.map(
                                    (p) => html`<option value=${p.slug}>${p.name}</option>`,
                                )}
                            </select>
                            <div class="form-text">
                                Leave this if you're not sure — we'll work it out.
                            </div>
                        </div>

                        <div class="mb-3">
                            <label class="form-label" for="title">Title</label>
                            <input
                                id="title"
                                class="form-control"
                                maxlength="200"
                                placeholder="One line — what happened, or what you want"
                                .value=${this._title}
                                @input=${(e) => {
                                    this._title = e.target.value;
                                }}
                            />
                        </div>

                        <div class="mb-3">
                            <community-markdown-input
                                input-id="body"
                                label="Details"
                                optional
                                rows="5"
                                placeholder=${
                                    this._type === 'bug'
                                        ? 'What did you do, what happened, and what did you expect instead?'
                                        : 'As much or as little as you like.'
                                }
                                .value=${this._body}
                                @value-changed=${(e) => {
                                    this._body = e.detail.value;
                                }}
                                @uploading-changed=${(e) => {
                                    this._uploading = e.detail.uploading;
                                }}
                            ></community-markdown-input>
                        </div>

                        ${
                            this._error
                                ? html`<div class="alert alert-danger py-2" role="alert">
                                      ${this._error}
                                  </div>`
                                : nothing
                        }

                        <div class="d-flex gap-2">
                            <button
                                class="btn btn-primary"
                                type="submit"
                                ?disabled=${!this._canSubmit}
                            >
                                ${
                                    this._submitting
                                        ? html`<span
                                                  class="spinner-border spinner-border-sm me-2"
                                                  role="status"
                                              ></span>Sending…`
                                        : this._uploading
                                          ? html`<span
                                                    class="spinner-border spinner-border-sm me-2"
                                                    role="status"
                                                ></span>Waiting for the image…`
                                          : html`<i class="bi bi-send me-1"></i>Send feedback`
                                }
                            </button>
                            ${
                                this.dismissible
                                    ? html`<button
                                          type="button"
                                          class="btn btn-link text-secondary"
                                          @click=${() => this.dispatchEvent(new CustomEvent('cancel'))}
                                      >
                                          Cancel
                                      </button>`
                                    : nothing
                            }
                        </div>
                    </form>
                </div>
            </div>
        `;
    }
}

customElements.define('community-give-feedback', CommunityGiveFeedback);
